import { jsx as _jsx, jsxs as _jsxs, Fragment as _Fragment } from "react/jsx-runtime";
import EditIcon from '@guildeducationinc/recess/icons/EditIcon';
import VisibilityIcon from '@guildeducationinc/recess/icons/VisibilityIcon';
import { EmailSections } from '@src/types';
import { DataTable, Messages, TrackableButton, useLocale, } from 'pe-core-frontend';
import { useMemo, useState } from 'react';
import { useNavigate } from 'react-router-dom';
export const CommunicationContentsList = ({ contents, tab = 'employees',
// showDeleteForOverrides = false,
// onDelete,
 }) => {
    const { t } = useLocale();
    const navigate = useNavigate();
    const [messages, setMessages] = useState([]);
    const [sortModel, setSortModel] = useState([
        { field: 'type', sort: 'asc' },
    ]);
    // const [deleteContent] = useMutation(DELETE_CONTENT);
    const handleEdit = (code) => {
        navigate(`/admin/comms/${tab}/email/${code}`);
    };
    const handleView = (code) => {
        navigate(`/admin/comms/${tab}/email/${code}?view=true`);
    };
    // Will be used later for deleting overrides from the detail view
    /* const handleDelete = async (contentId: number, contentName: string) => {
      if (
        !window.confirm(
          t('Are you sure you want to delete the override for "{name}"?', {
            values: { name: contentName },
          })
        )
      ) {
        return;
      }
  
      try {
        await deleteContent({
          variables: {
            id: contentId,
          },
        });
  
        setMessages([{ type: MessageType.SUCCESS, value: t('Override deleted successfully') }]);
  
        if (onDelete) {
          onDelete();
        }
      } catch (error) {
        setMessages([
          {
            type: MessageType.ERROR,
            value: t('Failed to delete override: {error}', {
              values: { error: String(error) },
            }),
          },
        ]);
      }
    }; */
    // Get type for a content code
    const getContentType = (code) => {
        const sections = EmailSections[tab] || {};
        for (const [typeKey, codes] of Object.entries(sections)) {
            if (codes.includes(code)) {
                return typeKey;
            }
        }
        return 'other';
    };
    // Check if content should be displayed in current tab
    const isContentInTab = (code) => {
        const sections = EmailSections[tab] || {};
        for (const codes of Object.values(sections)) {
            if (codes.includes(code)) {
                return true;
            }
        }
        return false;
    };
    // Get display name for type
    const getTypeDisplayName = (type) => {
        const typeMap = {
            authentication: 'Authentication',
            programRequest: 'Program Request',
            paymentRequest: 'Payment Request',
            payment: 'Payment',
            termination: 'Termination',
            export: 'Export',
            other: 'Other',
        };
        return typeMap[type] || type;
    };
    // Group and sort contents by code
    const groupedContents = useMemo(() => {
        const sortField = sortModel[0]?.field || 'type';
        const sortOrder = sortModel[0]?.sort || 'asc';
        // Filter contents for current tab
        const tabContents = contents.filter(content => isContentInTab(content.code));
        // Group by content code
        const byCode = {};
        tabContents.forEach(content => {
            if (!byCode[content.code]) {
                byCode[content.code] = [];
            }
            byCode[content.code].push(content);
        });
        // Create grouped content objects
        const groupedList = Object.entries(byCode).map(([code, codeContents]) => {
            const defaultContent = codeContents.find(c => !c.clientId || c.clientId === 1);
            const overrides = codeContents.filter(c => c.clientId && c.clientId !== 1);
            const distinctClients = new Set(overrides.map(c => c.clientId)).size;
            return {
                id: code, // DataTable requires an id field
                code,
                type: getContentType(code),
                name: defaultContent?.name || codeContents[0]?.name || code,
                defaultContent,
                overrideCount: distinctClients,
                allContents: codeContents,
            };
        });
        // Sort grouped contents
        const sorted = [...groupedList].sort((a, b) => {
            let compareResult = 0;
            if (sortField === 'type') {
                compareResult = a.type.localeCompare(b.type);
                // Secondary sort by name when sorting by type
                if (compareResult === 0) {
                    compareResult = a.name.localeCompare(b.name);
                }
            }
            else if (sortField === 'name') {
                compareResult = a.name.localeCompare(b.name);
            }
            else if (sortField === 'overrideCount') {
                compareResult = a.overrideCount - b.overrideCount;
            }
            return sortOrder === 'asc' ? compareResult : -compareResult;
        });
        // Insert type header rows
        const withHeaders = [];
        let currentType = '';
        sorted.forEach(content => {
            if (content.type !== currentType) {
                currentType = content.type;
                withHeaders.push({
                    id: `header-${content.type}`,
                    isHeader: true,
                    type: content.type,
                });
            }
            withHeaders.push(content);
        });
        return withHeaders;
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [contents, sortModel, tab]);
    return (_jsxs(_Fragment, { children: [_jsx(Messages, { messages: messages, onClose: () => setMessages([]) }), _jsx(DataTable, { disableRowSelectionOnClick: true, disableColumnMenu: true, rows: groupedContents, sortModel: sortModel, onSortModelChange: setSortModel, getRowClassName: params => (params.row.isHeader ? 'type-header-row' : ''), sx: {
                    '& .type-header-row': {
                        backgroundColor: '#f5f5f5',
                        fontWeight: 500,
                        '&:hover': {
                            backgroundColor: '#f5f5f5',
                        },
                    },
                    '& .type-header-row .MuiDataGrid-cell': {
                        fontWeight: 500,
                    },
                }, columns: [
                    {
                        field: 'type',
                        headerName: t('Type'),
                        flex: 0.2,
                        renderCell: params => {
                            if (params.row.isHeader) {
                                return getTypeDisplayName(params.value);
                            }
                            return '';
                        },
                    },
                    {
                        field: 'name',
                        headerName: t('Content Name'),
                        flex: 0.3,
                        renderCell: params => (params.row.isHeader ? '' : params.value),
                    },
                    {
                        field: 'overrideCount',
                        headerName: t('Has Client Overrides'),
                        flex: 0.3,
                        renderCell: params => {
                            if (params.row.isHeader)
                                return '';
                            return params.value > 0
                                ? `${params.value} ${params.value === 1 ? t('override') : t('overrides')}`
                                : t('No overrides');
                        },
                    },
                    {
                        field: 'actions',
                        headerName: t('Actions'),
                        width: 120,
                        sortable: false,
                        renderCell: ({ row }) => {
                            if (row.isHeader)
                                return '';
                            return (_jsxs("div", { children: [_jsx(TrackableButton, { btnType: 'icon', enText: 'View', tag: `content_view_${row.code}`, appearance: 'minimal', label: t('View'), icon: _jsx(VisibilityIcon, { size: 20 }), onClick: () => handleView(row.code), className: 'margin--right_8' }), _jsx(TrackableButton, { btnType: 'icon', enText: 'Edit', tag: `content_edit_${row.code}`, appearance: 'minimal', label: t('Edit'), icon: _jsx(EditIcon, { size: 20 }), onClick: () => handleEdit(row.code) })] }));
                        },
                    },
                ] })] }));
};
